import { API_BASE_URL, get, patch, postFormData } from './apiClient';

export type RfqStatus =
  | 'pending'
  | 'ready'
  | 'processing'
  | 'processed'
  | 'error'
  | 'nonrfq';

export interface RfqListItemDto {
  _id: string;
  rfqId?: string;
  customerName?: string;
  /** Real email sender (EmailMessage.from) — same value the inbox "All" tab shows.
   * Empty for manual RFQs with no linked email; UI falls back to customerName. */
  from?: string;
  vesselName?: string;
  subject?: string;
  receivedAt?: string;
  status: RfqStatus | string;
  processingMode: 'auto' | 'manual_upload' | 'portal_auto' | string;
  itemCount: number;
  badge?: string;
  // allow quote linkage to come through without breaking if backend includes
  quoteId?: string | null;
  quotationId?: string | null;
  /** Set when a Client Invoice already exists for this RFQ (see rfq.service.js:getRFQById). */
  invoiceId?: string | null;
  /** Set when a customer email requested item addition(s) auto-applied to the
   * linked quotation and it hasn't been reviewed/resent yet (see rfq.service.js:getRFQById). */
  pendingAmendment?: {
    addedAt: string;
    sourceEmailId?: string | null;
    addedItems: Array<{ itemName: string; quantity: number; uom?: string | null }>;
    reviewed: boolean;
  } | null;
}

type StatusCountsOld = {
  all?: number;
  pending: number;
  processed: number;
  error: number;
};

type StatusCountsNew = {
  new: number; // frontend label, backend might still be pending
  ready: number;
  processed: number;
  error: number;
  all?: number;
};

export interface RfqListResponse {
  data: RfqListItemDto[];
  total: number;
  page: number;
  limit: number;
  statusCounts: StatusCountsOld | StatusCountsNew | any;
}

export interface RfqAttachmentDto {
  filename: string;
  mimeType: string;
  size?: number;
  storagePath?: string;
  fileUrl?: string | null;
}

export interface RfqEmailAttachmentDto {
  filename: string;
  mimeType: string;
  size?: number;
  storagePath?: string;
  fileUrl?: string;
}

export interface RfqEmailDto {
  from?: string;
  to?: string;
  cc?: string;
  date?: string;
  bodyText?: string;
  bodyHtml?: string;
  rawBodyText?: string;
  rawBodyHtml?: string;
  cleanedBody?: string;
  attachments?: RfqEmailAttachmentDto[];
}

export interface RfqThreadMessageDto {
  _id: string;
  threadId?: string;
  direction: 'inbound' | 'outbound';
  sentVia: 'erp' | 'external';
  subject?: string;
  from?: string;
  to?: string;
  cc?: string;
  date?: string;
  receivedAt?: string;
  bodyText?: string;
  bodyHtml?: string;
  attachments?: RfqEmailAttachmentDto[];
}

export interface RfqDetailDto extends RfqListItemDto {
  notes?: string;
  extractedData?: {
    header?: Record<string, unknown>;
    items?: unknown[];
    ocrText?: string;
    [key: string]: unknown;
  };
  attachments?: RfqAttachmentDto[];
  email?: RfqEmailDto;
  thread?: RfqThreadMessageDto[];
}

/**
 * RFQ line item
 */
export interface RfqItemDto {
  _id: string;
  itemId: number;
  description: string;
  quantity?: number;
  uom?: string;
  splitQuantity?: number | null;
  splitUom?: string | null;
  splitFormula?: string;
  requestedQuantity?: number | null;
  requestedUnit?: string | null;
  suggestedQuantity?: number | null;
  suggestedUnit?: string | null;
  confidenceScore?: number;
  remarks?: string;
  internalRemark?: string;
}

/**
 * List RFQs with optional status filter and paging.
 * NOTE: backend expects internal statuses (pending/ready/processing/processed/error/nonrfq).
 */
export type RfqSort = 'newest' | 'oldest' | 'sender_az' | 'sender_za' | 'subject_az';

export async function fetchRfqList(
  status?: RfqStatus | 'all',
  page = 1,
  limit = 25,
  search?: string,
  // Optional window (BUG-09). Omit both and you get every RFQ ever received —
  // there is deliberately no default range and no cap.
  range?: { dateFrom?: string; dateTo?: string },
  sort?: RfqSort
) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (status && status !== 'all') params.set('status', status);
  if (search && search.trim()) params.set('search', search.trim());
  if (range?.dateFrom) params.set('dateFrom', range.dateFrom);
  if (range?.dateTo) params.set('dateTo', range.dateTo);
  if (sort) params.set('sort', sort);

  return get<RfqListResponse>(`/rfqs?${params.toString()}`);
}

// Backend shape: { rfq, email, thread } OR rfq directly
export async function fetchRfqDetail(id: string) {
  const raw = await get<{ rfq: any; email?: any; thread?: any[] } | RfqDetailDto>(`/rfqs/${id}`);

  if ((raw as any)._id) {
    return raw as RfqDetailDto;
  }

  const { rfq, email, thread } = raw as { rfq: any; email?: any; thread?: any[] };

  return {
    ...(rfq || {}),
    email,
    thread: thread || [],
  } as RfqDetailDto;
}

/**
 * Look up the RFQ record generated from a given inbound email, if one exists.
 * Returns null (instead of throwing) when the email hasn't been turned into an
 * RFQ, so callers can fall back to the plain email view.
 */
export async function fetchRfqDetailByEmailId(emailId: string): Promise<RfqDetailDto | null> {
  return fetchRfqDetailBy(`by-email/${emailId}`);
}

/**
 * Resolve the RFQ for a whole conversation. Prefer this over fetchRfqDetailByEmailId:
 * the inbox knows the thread, and RFQ.emailMessageId is pinned to whichever message
 * originally triggered the RFQ — so keying off the thread's latest message resolves
 * only by luck, and returns an arbitrary RFQ when a thread holds several.
 */
export async function fetchRfqDetailByThreadId(threadId: string): Promise<RfqDetailDto | null> {
  return fetchRfqDetailBy(`by-thread/${encodeURIComponent(threadId)}`);
}

async function fetchRfqDetailBy(path: string): Promise<RfqDetailDto | null> {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

  const res = await fetch(`${API_BASE_URL}/rfqs/${path}`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Request failed with status ${res.status}`);
  }

  const raw = await res.json();
  if (raw && raw._id) {
    return raw as RfqDetailDto;
  }

  const { rfq, email, thread } = raw as { rfq: any; email?: any; thread?: any[] };
  return {
    ...(rfq || {}),
    email,
    thread: thread || [],
  } as RfqDetailDto;
}

// Backend shape: plain array or { items: [...] }
export async function fetchRfqItems(id: string) {
  const raw = await get<RfqItemDto[] | { items: RfqItemDto[] }>(`/rfqs/${id}/items`);

  if (Array.isArray(raw)) {
    return raw;
  }

  return (raw as { items: RfqItemDto[] }).items ?? [];
}

/**
 * Update a single RFQ item (row-level edit).
 */
export async function updateRfqItem(id: string, payload: Partial<RfqItemDto>) {
  return patch<RfqItemDto>(`/rfq-items/${id}`, payload);
}

/**
 * Reprocess RFQ by ID (server uses body but we send empty form).
 */
export async function reprocessRfq(id: string) {
  return postFormData<RfqDetailDto>(`/rfqs/${id}/reprocess`, new FormData());
}

/**
 * Manual RFQ creation via file upload.
 */
export async function createManualRfqWithFile(formData: FormData) {
  return postFormData<RfqDetailDto>('/rfqs/manual-upload', formData);
}

export async function createManualRfqWithFileTest(formData: FormData) {
  return postFormData<RfqDetailDto>('/rfqs/manual-upload-test', formData);
}

/**
 * Split RFQ creation via separate pipeline.
 */
export async function createSplitRfqWithFile(formData: FormData) {
  return postFormData<RfqDetailDto>('/rfq-split/upload', formData);
}

export async function reuploadRfqWithFile(id: string, formData: FormData) {
  return postFormData<RfqDetailDto>(`/rfqs/${id}/reupload`, formData);
}

/**
 * Delete RFQ – uses native fetch so apiClient stays untouched.
 */
export async function deleteRfq(id: string): Promise<void> {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

  const res = await fetch(`${API_BASE_URL}/rfqs/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Request failed with status ${res.status}`);
  }
}

/**
 * Update RFQ header/meta (subject, rfqId, customerName, vesselName, notes, status, assignedTo, etc.).
 * This maps to PATCH /api/rfqs/:id
 */
export async function updateRfq(
  id: string,
  payload: Partial<
    Pick<RfqDetailDto, 'rfqId' | 'customerName' | 'vesselName' | 'subject' | 'notes' | 'status'>
  > & {
    assignedTo?: string | null;
  }
) {
  return patch<RfqDetailDto>(`/rfqs/${id}`, payload);
}

/**
 * Replace ALL items for an RFQ.
 * This maps to PATCH /api/rfqs/:id/items
 */
export async function replaceRfqItems(
  id: string,
  items: Array<
    Pick<RfqItemDto, 'description'> &
      Partial<Pick<RfqItemDto, 'quantity' | 'uom' | 'confidenceScore' | 'remarks' | 'internalRemark'>>
  >
) {
  return patch<{ rfq: RfqDetailDto; items: RfqItemDto[] }>(`/rfqs/${id}/items`, {
    items,
  });
}

// Download a printable/filable AQUA MARIS-branded copy of the received RFQ as PDF.
export function downloadRfqPDF(id: string, rfqId?: string) {
  const token = localStorage.getItem('token');
  const url = `${API_BASE_URL}/rfqs/${id}/download/pdf`;

  return fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `RFQ_${rfqId || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch((error) => {
      console.error('Error downloading RFQ PDF:', error);
      throw error;
    });
}

// Download a printable/filable AQUA MARIS-branded copy of the received RFQ as Excel.
export function downloadRfqExcel(id: string, rfqId?: string) {
  const token = localStorage.getItem('token');
  const url = `${API_BASE_URL}/rfqs/${id}/download/excel`;

  return fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `RFQ_${rfqId || id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch((error) => {
      console.error('Error downloading RFQ Excel:', error);
      throw error;
    });
}
